import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FieldArray } from 'redux-form';

import {
  constraintSetType,
  intentType,
  entityType,
  fieldsType,
  featureType,
} from 'types';

import Header from './components/Header';
import ConditionFieldArray from './components/ConditionFieldArray';
import RefIntent from './components/RefIntent';

import './style.scss';

class ConstraintSetField extends Component {
  static propTypes = {
    allEntities: PropTypes.arrayOf(entityType),
    allIntents: PropTypes.arrayOf(intentType),
    change: PropTypes.func,
    featureTypes: PropTypes.arrayOf(featureType),
    fields: fieldsType,
    getAllEntities: PropTypes.func,
    getAllIntents: PropTypes.func,
    index: PropTypes.number,
    isLoadingAllEntities: PropTypes.bool,
    isLoadingAllIntents: PropTypes.bool,
    item: constraintSetType,
    name: PropTypes.string,
    /* eslint-disable-next-line react/forbid-prop-types */
    refElement: PropTypes.object,
    onScroll: PropTypes.func,
  };

  state = {
    isExpanded: true,
  };

  getNextFeatureToAdd = (totalFeatures, existingFeatures) =>
    totalFeatures.find(
      feature =>
        feature.public && existingFeatures.every(f => f.feature !== feature.id),
    ).id;

  handleToggleExpand = e => {
    e.stopPropagation();

    this.setState(prevState => ({
      isExpanded: !prevState.isExpanded,
    }));
  };

  handleAddRefIntent = () => {
    this.updateFields({
      intent_reference: '',
      intent_reference_obj: {},
      additional_intent_references: [],
      additional_intent_references_obj: [],
    });
  };

  handleOnDeleteRefIntent = () => {
    this.updateFields({
      all_references: null,
      intent_reference: null,
      intent_reference_obj: null,
      additional_intent_references: [],
      additional_intent_references_obj: [],
    });
  };

  handleRefIntentRemoveItem = intentsNames => {
    const { allIntents } = this.props;

    if (intentsNames.length === 0) {
      this.updateFields({
        intent_reference: '',
        intent_reference_obj: null,
        additional_intent_references: [],
        additional_intent_references_obj: [],
      });
    } else if (intentsNames.length === 1) {
      const intentRef = allIntents.find(
        intent => intent.name === intentsNames[0],
      );

      this.updateFields({
        intent_reference: intentRef.id,
        intent_reference_obj: {
          name: intentsNames[0],
          id: intentRef.id,
          archived: intentRef.archived,
        },
        additional_intent_references: [],
        additional_intent_references_obj: [],
      });
    } else {
      const intentRefs = allIntents
        .filter(intent => intentsNames.includes(intent.name))
        .map(intentRef => ({
          name: intentRef.name,
          id: intentRef.id,
          archived: intentRef.archived,
        }));

      const firstIntentRef = intentRefs[0];
      const restIntentRefs = intentRefs.slice(1);

      this.updateFields({
        intent_reference: firstIntentRef.id,
        intent_reference_obj: firstIntentRef,
        additional_intent_references: [
          ...restIntentRefs.map(intentRef => intentRef.id),
        ],
        additional_intent_references_obj: restIntentRefs,
      });
    }
  };

  handleRefIntentSelectItem = intent => {
    const { fields, index } = this.props;
    const field = fields.get(index);
    const additionals = field.additional_intent_references || [];

    if (!field.intent_reference) {
      this.updateFields({
        intent_reference: intent.id,
        intent_reference_obj: {
          name: intent.name,
          id: intent.id,
          archived: intent.archived,
        },
        additional_intent_references: [],
        additional_intent_references_obj: [],
      });
    } else if (!additionals.includes(intent.id))
      this.updateFields({
        additional_intent_references: [...additionals, intent.id],
        additional_intent_references_obj: [
          ...(field.additional_intent_references_obj || []),
          { name: intent.name, id: intent.id, archived: intent.archived },
        ],
      });
  };

  handleEntitySelectItem = (entity, entityIndex) => {
    const { fields, index, featureTypes } = this.props;
    const field = fields.get(index);
    this.updateFields({
      conditions: field.conditions.map((condition, _index) => {
        if (_index === entityIndex) {
          return {
            ...condition,
            entity: entity.id,
            entity_reference_obj: {
              ...entity,
              featuresMap: entity.features.reduce((acc, item) => {
                if (item.public) {
                  return {
                    ...acc,
                    [item.id]: {
                      ...item,
                      available_values: featureTypes.find(
                        featureType => featureType.name === item.feature_type,
                      ).available_values,
                    },
                  };
                }
                return acc;
              }, {}),
            },
            features: [],
          };
        }
        return condition;
      }),
    });
  };

  handleDeleteCondition = entityIndex => {
    const { fields, index } = this.props;
    const field = fields.get(index);
    this.updateFields({
      conditions: field.conditions.filter(
        (condition, index) => index !== entityIndex,
      ),
    });
  };

  // TODO: find out if there is another way to update fields
  updateFields = updates => {
    const { fields, index } = this.props;
    const field = fields.get(index);
    fields.remove(index);

    fields.push({
      ...field,
      ...updates,
    });
    fields.move(fields.length - 1, index);
  };

  handleAddEntity = () => {
    const { fields, index, onScroll } = this.props;
    const field = fields.get(index);
    this.updateFields({
      conditions: [
        ...field.conditions,
        {
          entity: '',
          entity_reference_obj: {},
        },
      ],
    });
    onScroll(this.field, 'entity');
  };

  handleAddFeature = entityIndex => {
    const { fields, index, item } = this.props;
    const field = fields.get(index);
    const _condition = item.conditions[entityIndex];
    this.updateFields({
      conditions: field.conditions.map((condition, _index) => {
        if (_index === entityIndex) {
          return {
            ...condition,
            features: [
              ...condition.features,
              {
                equals_values: [],
                feature: this.getNextFeatureToAdd(
                  _condition.entity_reference_obj.features,
                  condition.features,
                ),
              },
            ],
          };
        }
        return condition;
      }),
    });
  };

  handleDeleteFeature = (entityIndex, feature) => {
    const { fields, index } = this.props;
    const field = fields.get(index);
    this.updateFields({
      conditions: field.conditions.map((condition, _index) => {
        if (_index === entityIndex) {
          return {
            ...condition,
            features: condition.features.filter(
              _feature => _feature.feature !== feature.feature,
            ),
          };
        }
        return condition;
      }),
    });
  };

  handleChangeFeatureValues = (entityIndex, value, feature) => {
    const { fields, index } = this.props;
    const field = fields.get(index);
    this.updateFields({
      conditions: field.conditions.map((condition, _index) => {
        if (_index === entityIndex) {
          return {
            ...condition,
            features: condition.features.map(_feature => {
              if (_feature.feature === feature.feature) {
                return {
                  ..._feature,
                  equals_values: value,
                };
              }
              return _feature;
            }),
          };
        }
        return condition;
      }),
    });
  };

  handleDeleteConstraintSet = async () => {
    const { fields, index } = this.props;

    await fields.remove(index);
    this.updateNames();
  };

  handleDuplicateConstraintSet = async () => {
    const { fields, index, onScroll, refElement } = this.props;

    await fields.insert(index, {
      ...fields.get(index),
      name: `ConstraintSet-${fields.length + 1}`,
    });
    fields.move(index, fields.length);
    onScroll(refElement, '');
  };

  updateNames = () => {
    const { fields, change } = this.props;

    fields.forEach((field, index) => {
      if (fields.get(index)) {
        change(`${field}.name`, `ConstraintSet-${index + 1}`);
      }
    });
  };

  handleContainerRef = ref => {
    this.field = ref;
  };

  render() {
    const {
      item,
      name,
      change,
      isLoadingAllIntents,
      isLoadingAllEntities,
      allIntents,
      allEntities,
      getAllIntents,
      getAllEntities,
    } = this.props;
    const { isExpanded } = this.state;
    const { fields, index } = this.props;
    const field = fields.get(index);

    return (
      <div ref={this.handleContainerRef}>
        <Header
          addEntity={this.handleAddEntity}
          addRefIntent={this.handleAddRefIntent}
          isExpanded={isExpanded}
          item={item}
          toggleExpand={this.handleToggleExpand}
          onDelete={this.handleDeleteConstraintSet}
          onDuplicate={this.handleDuplicateConstraintSet}
        />
        {isExpanded && (
          <div styleName="constraint-set">
            {/* intent_reference has to be not null and undefined
                The intent_reference is initialized with '' 
                which is treated as false */}
            {item.intent_reference !== null &&
              item.intent_reference !== undefined && (
                <RefIntent
                  additionalIntents={item.additional_intent_references_obj}
                  allIntents={allIntents}
                  change={change}
                  getAllIntents={getAllIntents}
                  intent={item.intent_reference_obj}
                  isLoadingAllIntents={isLoadingAllIntents}
                  name={name}
                  removeItem={this.handleRefIntentRemoveItem}
                  selectedIntents={field.all_references || []}
                  selectItem={this.handleRefIntentSelectItem}
                  onDelete={this.handleOnDeleteRefIntent}
                />
              )}

            <FieldArray
              allEntities={allEntities}
              component={ConditionFieldArray}
              getAllEntities={getAllEntities}
              isLoadingAllEntities={isLoadingAllEntities}
              name={`${name}.conditions`}
              selectItem={this.handleEntitySelectItem}
              onAddFeature={this.handleAddFeature}
              onChangeFeatureValues={this.handleChangeFeatureValues}
              onDelete={this.handleDeleteCondition}
              onDeleteFeature={this.handleDeleteFeature}
            />
          </div>
        )}
      </div>
    );
  }
}

export default ConstraintSetField;
